import { FC, useMemo, useState } from 'react';

import type { TeamActivityMember } from '@/api/gamma-team/types';
import { Tabs, TextButton, theme, Typography } from '@/design-system';

import { SINGLE_SERIES_COLOR } from './chart-palette';
import {
  BarFill,
  BarList,
  BarRowButton,
  BarTrack,
  ChartCard,
  ChartHeader,
  SectionTitle,
  SkeletonBar,
} from './styled';
import { memberName } from './team-sorting';
import { dealMoves } from './team-trends';

export const COMPARISON_TITLE = 'Who did the most';

const comparisonMetric = {
  Contacts: 'contacts',
  Entries: 'entries',
  Deals: 'deals',
} as const;
type ComparisonMetric = (typeof comparisonMetric)[keyof typeof comparisonMetric];

const METRIC_VALUE: Record<ComparisonMetric, (_member: TeamActivityMember) => number> = {
  contacts: (member) => member.contactsReached,
  entries: (member) => member.entries,
  deals: (member) => dealMoves(member),
};

const METRIC_UNIT: Record<ComparisonMetric, string> = {
  contacts: 'contacts reached',
  entries: 'entries',
  deals: 'deal moves',
};

const DEFAULT_ROWS = 8;

interface MemberComparisonProps {
  members: TeamActivityMember[] | null;
  rowLimit?: number;
  onSelectMember: (_userId: string) => void;
}

export const MemberComparison: FC<MemberComparisonProps> = ({
  members,
  rowLimit = DEFAULT_ROWS,
  onSelectMember,
}) => {
  const limit = Math.max(1, rowLimit);
  const [metric, setMetric] = useState<ComparisonMetric>(comparisonMetric.Contacts);
  const [expanded, setExpanded] = useState(false);

  const ranked = useMemo(() => {
    if (!members) return null;
    const valueOf = METRIC_VALUE[metric];
    return members
      .map((member) => ({ member, value: valueOf(member) }))
      .filter(({ value }) => value > 0)
      .sort(
        (a, b) => b.value - a.value || memberName(a.member).localeCompare(memberName(b.member)),
      );
  }, [members, metric]);

  const max = ranked?.[0]?.value ?? 0;
  const shown = ranked && !expanded ? ranked.slice(0, limit) : ranked;
  const hiddenCount = ranked ? ranked.length - (shown?.length ?? 0) : 0;

  return (
    <ChartCard aria-label={COMPARISON_TITLE}>
      <ChartHeader>
        <SectionTitle>
          <Typography variant="body1" fontWeight={600}>
            {COMPARISON_TITLE}
          </Typography>
          <Typography variant="body4" color={theme.colors.text.secondary}>
            {`Ranked by ${METRIC_UNIT[metric]} in the period`}
          </Typography>
        </SectionTitle>
        <Tabs.Root
          value={metric}
          onValueChange={(value) => setMetric(value as ComparisonMetric)}
          size="sm"
          variant="boxed"
        >
          <Tabs.List>
            <Tabs.Tab value={comparisonMetric.Contacts} label="Contacts" />
            <Tabs.Tab value={comparisonMetric.Entries} label="Entries" />
            <Tabs.Tab value={comparisonMetric.Deals} label="Deals" />
          </Tabs.List>
        </Tabs.Root>
      </ChartHeader>

      {!shown ? (
        <BarList aria-hidden>
          {[0, 1, 2, 3].map((index) => (
            <li key={index}>
              <SkeletonBar h={20} />
            </li>
          ))}
        </BarList>
      ) : shown.length === 0 ? (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {`No ${METRIC_UNIT[metric]} in this period.`}
        </Typography>
      ) : (
        <BarList>
          {shown.map(({ member, value }) => {
            const name = memberName(member);
            return (
              <li key={member.userId}>
                <BarRowButton
                  type="button"
                  onClick={() => onSelectMember(member.userId)}
                  aria-label={`${name}: ${value.toLocaleString()} ${METRIC_UNIT[metric]}. Open details`}
                >
                  <Typography variant="body3" color={theme.colors.text.primary} noWrap>
                    {name}
                  </Typography>
                  <BarTrack>
                    <BarFill share={max ? value / max : 0} color={SINGLE_SERIES_COLOR} />
                    <Typography variant="body4" color={theme.colors.text.secondary}>
                      {value.toLocaleString()}
                    </Typography>
                  </BarTrack>
                </BarRowButton>
              </li>
            );
          })}
        </BarList>
      )}

      {(hiddenCount > 0 || expanded) && ranked && ranked.length > limit && (
        <TextButton color="neutral" onClick={() => setExpanded((current) => !current)}>
          {expanded ? `Show top ${limit}` : `Show all ${ranked.length}`}
        </TextButton>
      )}
    </ChartCard>
  );
};
